import { AUTH_UI_MESSAGES, translateAuthMessage } from "@/constants/auth";
import { AUTH_PATHS, appPath } from "@/constants/paths";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { LoadingButton } from "@/components/shared/loader";
import { useResendOtp, useVerifyOtp } from "@/features/auth/store";
import { verifyOtpSchema } from "@/validations/login";
import type { z } from "zod";
import { cn } from "@/lib/utils";
import { isAdmin } from "@/lib/auth-utils";
import type { User } from "@/types/user";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { getErrorMessage } from "@/lib/error-handler";
import { AlertCircle, ArrowRight } from "lucide-react";

type VerifyOtpSchema = z.infer<typeof verifyOtpSchema>;

type OtpLocationState = {
  message?: string | null;
};

const RESEND_COOLDOWN_SECONDS = 60;

export default function VerifyOtpForm() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const verifyOtp = useVerifyOtp();
  const resendOtp = useResendOtp();

  const email = searchParams.get("email")?.trim() ?? "";
  const initialOtpMessage = translateAuthMessage(
    (location.state as OtpLocationState | null)?.message,
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [infoMessage, setInfoMessage] = useState<string | null>(
    initialOtpMessage,
  );
  const [resendCooldown, setResendCooldown] = useState(0);
  const [isResending, setIsResending] = useState(false);

  const form = useForm<VerifyOtpSchema>({
    resolver: zodResolver(verifyOtpSchema),
    mode: "onChange",
    defaultValues: {
      otp: "",
    },
  });

  const {
    handleSubmit,
    control,
    formState: { isSubmitting },
  } = form;

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = window.setTimeout(() => {
      setResendCooldown((value) => value - 1);
    }, 1000);
    return () => window.clearTimeout(timer);
  }, [resendCooldown]);

  const handleAdminRedirect = (user: User | null | undefined) => {
    if (user && !isAdmin(user)) {
      setErrorMessage(
        "غير مصرح لك بالوصول إلى لوحة الإدارة. هذا المحتوى متاح للمدراء فقط.",
      );
      return;
    }

    if (user?.mustChangePassword) {
      navigate(AUTH_PATHS.REQUIRED_PASSWORD_CHANGE, { replace: true });
      return;
    }

    navigate(appPath());
  };

  const onSubmit = async (data: VerifyOtpSchema) => {
    setErrorMessage(null);

    try {
      const response = await verifyOtp({
        email,
        otp: data.otp,
      });

      handleAdminRedirect(response.data.user);
    } catch (error) {
      setErrorMessage(getErrorMessage(error));
    }
  };

  const handleResend = async () => {
    if (!email || resendCooldown > 0 || isResending) return;

    setErrorMessage(null);
    setIsResending(true);

    try {
      const response = await resendOtp(email);
      setInfoMessage(
        translateAuthMessage(response.message) ?? AUTH_UI_MESSAGES.OTP_SENT,
      );
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
      form.setValue("otp", "");
    } catch (error) {
      setErrorMessage(getErrorMessage(error));
    } finally {
      setIsResending(false);
    }
  };

  if (!email) {
    return (
      <div
        className={cn(
          "flex flex-col bg-card p-4 rounded-lg shadow w-full max-w-md",
        )}
      >
        <div className="mb-4 space-y-1 text-center">
          <h2 className="text-lg font-semibold text-foreground">
            رابط غير صالح
          </h2>
          <p className="text-sm text-muted-foreground">
            يرجى تسجيل الدخول مرة أخرى لإرسال رمز التحقق.
          </p>
        </div>

        <Button asChild className="w-full">
          <Link to={AUTH_PATHS.LOGIN}>العودة لتسجيل الدخول</Link>
        </Button>
      </div>
    );
  }

  return (
    <Form {...form}>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className={cn(
          "flex flex-col bg-card p-4 rounded-lg shadow w-full max-w-md",
        )}
      >
        <div className="mb-4 space-y-1 text-center">
          <h2 className="text-lg font-semibold text-foreground">
            أدخل رمز التحقق
          </h2>
          <p className="text-sm text-muted-foreground">
            تم إرسال رمز مكون من 6 أرقام إلى{" "}
            <span className="font-medium text-foreground">{email}</span>
          </p>
        </div>

        {infoMessage && (
          <div className="mb-3 rounded-md border border-primary/20 bg-primary/5 px-3 py-2 text-sm text-foreground">
            {infoMessage}
          </div>
        )}

        {errorMessage && (
          <div className="flex items-start gap-2 mb-3 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{errorMessage}</span>
          </div>
        )}

        <FormField
          control={control}
          name="otp"
          render={({ field }) => (
            <FormItem className="flex flex-col gap-2">
              <FormLabel className="text-sm font-medium text-foreground">
                رمز التحقق
              </FormLabel>
              <FormControl>
                <Input
                  {...field}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  placeholder="123456"
                  className="text-center text-lg tracking-[0.35em]"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button
          type="submit"
          disabled={isSubmitting}
          className={cn("w-full h-10 mt-4 text-white font-medium")}
        >
          <LoadingButton
            loading={isSubmitting}
            loadingText="جار التحقق..."
            loaderSize="sm"
          >
            تأكيد الرمز
          </LoadingButton>
        </Button>

        <Button
          type="button"
          variant="outline"
          disabled={isResending || resendCooldown > 0}
          onClick={handleResend}
          className="mt-2 w-full"
        >
          <LoadingButton
            loading={isResending}
            loadingText="جار إرسال الرمز..."
            loaderSize="sm"
          >
            {resendCooldown > 0
              ? `إعادة إرسال الرمز (${resendCooldown}s)`
              : "إعادة إرسال الرمز"}
          </LoadingButton>
        </Button>

        <Button asChild variant="ghost" className="mt-2">
          <Link to={AUTH_PATHS.LOGIN}>
            <ArrowRight className="ml-2 h-4 w-4" />
            العودة لتسجيل الدخول
          </Link>
        </Button>
      </form>
    </Form>
  );
}
